<script lang="ts" module>
	export type OnVerify = (token: string) => void;
</script>

<script lang="ts">
	import { onMount } from 'svelte';
	import { twMerge } from 'tailwind-merge';
	import { Txt } from '../Txt';
	import type { TurnstileAppearance, TurnstileSize, TurnstileTheme } from './types';

	interface Props {
		publicTurnstileKey: string;
		onVerify?: OnVerify;
		onError?: () => void;
		onExpired?: () => void;
		onTimeout?: () => void;
		theme?: TurnstileTheme;
		size?: TurnstileSize;
		appearance?: TurnstileAppearance;
		action?: string;
		cData?: string;
		language?: string;
		maxRetries?: number;
		reset?: () => void;
		hasError?: boolean;
		dataCy?: string;
		classes?: { container?: string };
	}

	let {
		publicTurnstileKey,
		onVerify = () => void 0,
		onError = () => void 0,
		onExpired = () => void 0,
		onTimeout = () => void 0,
		theme = 'auto',
		size = 'normal',
		appearance = 'always',
		action = undefined,
		cData = undefined,
		language = undefined,
		maxRetries = 3,
		reset = $bindable(),
		hasError = false,
		dataCy = 'turnstile',
		classes = {}
	}: Props = $props();

	const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
	const SCRIPT_TIMEOUT_MS = 3000;

	let container: HTMLDivElement | undefined = $state(undefined);
	let widgetId = '';
	let retries = 0;

	function loadTurnstileScript() {
		return new Promise<void>((resolve) => {
			if (window.turnstile) {
				resolve();
				return;
			}

			const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);

			if (existing) {
				existing.addEventListener('load', () => resolve());
				return;
			}

			const script = document.createElement('script');
			script.defer = true;
			script.src = SCRIPT_SRC;
			script.onload = () => resolve();
			document.head.appendChild(script);
		});
	}

	function waitForTurnstile() {
		return new Promise<typeof window.turnstile | null>((resolve) => {
			const startTime = performance.now();

			const check = () => {
				if (window.turnstile) {
					resolve(window.turnstile);
					return;
				}

				if (performance.now() - startTime > SCRIPT_TIMEOUT_MS) {
					console.error('Turnstile failed to load within 3 seconds');
					resolve(null);
					return;
				}

				setTimeout(check, 100);
			};

			check();
		});
	}

	function resetWidget() {
		if (widgetId && window.turnstile) {
			try {
				window.turnstile.reset(widgetId);
			} catch (err) {
				// Widget may have been removed, ignore the error.
				console.debug('Turnstile reset failed:', err);
			}
		}
	}

	// Retry automatically up to `maxRetries`, then notify the parent via `callback`.
	function retryOrNotify(callback: () => void) {
		if (retries < maxRetries) {
			retries++;
			resetWidget();
		} else {
			callback();
		}
	}

	onMount(() => {
		reset = () => {
			retries = 0;
			resetWidget();
		};

		const render = async () => {
			await loadTurnstileScript();
			const turnstile = await waitForTurnstile();

			if (!turnstile || !container) {
				onError();
				return;
			}

			try {
				widgetId = turnstile.render(container, {
					sitekey: publicTurnstileKey,
					theme,
					size,
					appearance,
					...(action ? { action } : {}),
					...(cData ? { cData } : {}),
					...(language ? { language } : {}),
					callback: (token: string) => {
						retries = 0;
						onVerify(token);
					},
					'error-callback': () => retryOrNotify(onError),
					'expired-callback': () => retryOrNotify(onExpired),
					'timeout-callback': () => retryOrNotify(onTimeout)
				});
			} catch (err) {
				console.debug('Turnstile render failed:', err);
				onError();
			}
		};

		render();

		return () => {
			if (widgetId && window.turnstile) {
				try {
					window.turnstile.remove(widgetId);
				} catch (err) {
					console.debug('Turnstile remove failed:', err);
				}
				widgetId = '';
			}
		};
	});
</script>

<div class={twMerge('flex flex-col items-center', classes.container)}>
	<div bind:this={container} data-cy={`${dataCy}-widget`}></div>

	{#if hasError}
		<Txt
			dataCy={`${dataCy}-error`}
			variant="body3"
			class="mt-2 text-center font-[500] text-red-500"
		>
			Verification failed. Please try again.
		</Txt>
	{/if}
</div>
